import { useCallback } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { useAIEventsInstrumentation } from '@atlassian/ai-analytics';

import { useBulkCreateContext } from '../providers/BulkCreateContextProvider.tsx';
import {
	type PageMode,
	useIssueCreateSidePanelContext,
} from '../providers/IssueCreateSidePanelContextProvider.tsx';
import { useMappingsState } from '../providers/BulkConfigureContextProvider.tsx';

type ActionType = 'createAll' | 'createSingleIssue';

export const HIGHLIGHT_ISSUE_CREATE_SOURCE = 'confluenceHighlightIssueCreate';
export const BULK_ISSUE_CREATE_SOURCE = 'confluenceBulkIssueCreate';
export const AI_BULK_ISSUE_CREATE_FEATURE_NAME = 'aiIssueCreateConfluenceBulk';

type BuildAttributesParams = {
	baseAttributes: Record<string, any>;
	isCreatedWithAi: boolean;
	isCreatedFromTable: boolean;
	hasEditPermission: boolean;
	isCreatedFromActionItems: boolean;
	pageMode?: PageMode;
};

const buildAttributes = ({
	baseAttributes,
	isCreatedWithAi,
	isCreatedFromTable,
	hasEditPermission,
	isCreatedFromActionItems,
	pageMode,
}: BuildAttributesParams) => {
	baseAttributes.isCreatedFromTable = isCreatedFromTable;
	baseAttributes.isCreatedWithAi = isCreatedWithAi;
	baseAttributes.hasEditPermission = hasEditPermission;
	baseAttributes.isCreatedFromActionItems = isCreatedFromActionItems;
	if (pageMode) {
		baseAttributes.pageMode = pageMode;
	}
	if (isCreatedWithAi) {
		return { ...baseAttributes, aiFeatureName: AI_BULK_ISSUE_CREATE_FEATURE_NAME };
	}
	return baseAttributes;
};

export const getSource = (isCreatedFromTable: boolean, isCreatedFromActionItems?: boolean) => {
	return isCreatedFromTable || isCreatedFromActionItems
		? HIGHLIGHT_ISSUE_CREATE_SOURCE
		: BULK_ISSUE_CREATE_SOURCE;
};

const getErrorMessage = (error: Error | unknown) => {
	if (error instanceof Error) return error.message;
	return String(error);
};

// Returns the object with number of mappings if they exist, otherwise returns object with undefined
export const useNumMappings = () => {
	const { columnToFieldMappings } = useMappingsState();
	const numMappings = columnToFieldMappings ? columnToFieldMappings.length : undefined;

	return { numMappings };
};

export const useBulkCreateLoggingEvents = () => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const {
		trackAIResultError,
		trackAIInteractionInit,
		trackAIResultView,
		trackAIResultAction,
		trackAIInteractionDismiss,
	} = useAIEventsInstrumentation();
	const [{ isCreatedWithAi, isCreatedFromTable, isCreatedFromActionItems }] =
		useBulkCreateContext();
	const [{ pageMode, hasEditPermission }] = useIssueCreateSidePanelContext();
	const { numMappings } = useNumMappings();

	const logMultipleIssueCreateSuccess = useCallback(
		(numIssues: number) => {
			const attributes = buildAttributes({
				baseAttributes: { numIssues, ...(numMappings && { numMappings }) },
				isCreatedWithAi,
				isCreatedFromTable,
				isCreatedFromActionItems,
				hasEditPermission,
				pageMode,
			});
			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					source: getSource(isCreatedFromTable, isCreatedFromActionItems),
					action: 'created',
					actionSubject: 'issue',
					attributes,
				},
			}).fire();
		},
		[
			numMappings,
			createAnalyticsEvent,
			isCreatedWithAi,
			isCreatedFromTable,
			isCreatedFromActionItems,
			pageMode,
			hasEditPermission,
		],
	);

	const logMultipleIssueCreateFailure = useCallback(
		(numIssues: number, error: Error | unknown) => {
			const attributes = buildAttributes({
				baseAttributes: { error: getErrorMessage(error), numIssues },
				isCreatedWithAi,
				isCreatedFromTable,
				isCreatedFromActionItems,
				hasEditPermission,
				pageMode,
			});
			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					source: getSource(isCreatedFromTable, isCreatedFromActionItems),
					action: 'failed',
					actionSubject: 'issueCreation',
					attributes,
				},
			}).fire();
		},
		[
			createAnalyticsEvent,
			isCreatedWithAi,
			isCreatedFromTable,
			isCreatedFromActionItems,
			pageMode,
			hasEditPermission,
		],
	);

	const logIssueInsertionFailure = useCallback(
		(numIssues: number, error: Error | unknown) => {
			const attributes = buildAttributes({
				baseAttributes: { error: getErrorMessage(error), numIssues },
				isCreatedWithAi,
				isCreatedFromTable,
				isCreatedFromActionItems,
				hasEditPermission,
				pageMode,
			});
			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					source: getSource(isCreatedFromTable, isCreatedFromActionItems),
					action: 'failed',
					actionSubject: 'issueInsertion',
					attributes,
				},
			}).fire();
		},
		[
			createAnalyticsEvent,
			isCreatedWithAi,
			isCreatedFromTable,
			isCreatedFromActionItems,
			pageMode,
			hasEditPermission,
		],
	);

	const logSingleIssueCreateSuccess = useCallback(() => {
		logMultipleIssueCreateSuccess(1);
	}, [logMultipleIssueCreateSuccess]);

	const logSingleIssueCreateFailure = useCallback(
		(error: Error | unknown) => {
			const attributes = buildAttributes({
				baseAttributes: { error: getErrorMessage(error) },
				isCreatedWithAi,
				isCreatedFromTable,
				isCreatedFromActionItems,
				hasEditPermission,
				pageMode,
			});
			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					source: getSource(isCreatedFromTable, isCreatedFromActionItems),
					action: 'error',
					actionSubject: 'globalIssueCreate',
					attributes,
				},
			}).fire();
		},
		[
			createAnalyticsEvent,
			isCreatedWithAi,
			isCreatedFromTable,
			isCreatedFromActionItems,
			pageMode,
			hasEditPermission,
		],
	);

	const logCancelAiPanel = useCallback(() => {
		const attributes = buildAttributes({
			baseAttributes: {},
			isCreatedWithAi,
			isCreatedFromTable,
			isCreatedFromActionItems,
			hasEditPermission,
			pageMode,
		});
		createAnalyticsEvent({
			type: 'sendTrackEvent',
			data: {
				source: getSource(isCreatedFromTable, isCreatedFromActionItems),
				action: 'cancelled',
				actionSubject: 'aiPanel',
				attributes,
			},
		}).fire();
	}, [
		isCreatedWithAi,
		isCreatedFromTable,
		isCreatedFromActionItems,
		hasEditPermission,
		pageMode,
		createAnalyticsEvent,
	]);

	const logAiResultViewed = useCallback(
		(issuesGenerated: number) => {
			const attributes = buildAttributes({
				baseAttributes: { issuesGenerated },
				isCreatedWithAi,
				isCreatedFromTable,
				isCreatedFromActionItems,
				hasEditPermission,
				pageMode,
			});
			trackAIResultView({ attributes });
		},
		[
			trackAIResultView,
			isCreatedWithAi,
			isCreatedFromTable,
			isCreatedFromActionItems,
			pageMode,
			hasEditPermission,
		],
	);

	const logAiResultActioned = useCallback(
		(aiResultAction: ActionType, issueId?: string) => {
			const attributes = buildAttributes({
				baseAttributes: {},
				isCreatedWithAi,
				isCreatedFromTable,
				isCreatedFromActionItems,
				hasEditPermission,
				pageMode,
			});
			trackAIResultAction(aiResultAction, { attributes: { ...attributes, issueId } });
		},
		[
			isCreatedFromTable,
			isCreatedWithAi,
			isCreatedFromActionItems,
			pageMode,
			trackAIResultAction,
			hasEditPermission,
		],
	);

	const logAiResultRemoved = useCallback(() => {
		const attributes = buildAttributes({
			baseAttributes: {},
			isCreatedWithAi,
			isCreatedFromTable,
			isCreatedFromActionItems,
			hasEditPermission,
			pageMode,
		});
		createAnalyticsEvent({
			type: 'sendTrackEvent',
			data: {
				source: getSource(isCreatedFromTable, isCreatedFromActionItems),
				action: 'removed',
				actionSubject: 'aiResult',
				attributes,
			},
		}).fire();
	}, [
		createAnalyticsEvent,
		isCreatedFromTable,
		isCreatedWithAi,
		isCreatedFromActionItems,
		pageMode,
		hasEditPermission,
	]);

	const logRequestFailed = useCallback(
		({ requestName, errorMessage }: { requestName: string; errorMessage: string }) => {
			const attributes = buildAttributes({
				baseAttributes: { errorMessage },
				isCreatedWithAi,
				isCreatedFromTable,
				isCreatedFromActionItems,
				hasEditPermission,
				pageMode,
			});
			createAnalyticsEvent({
				type: 'sendOperationalEvent',
				data: {
					source: getSource(isCreatedFromTable, isCreatedFromActionItems),
					action: 'failed',
					actionSubject: requestName,
					attributes,
				},
			}).fire();
		},
		[
			createAnalyticsEvent,
			isCreatedFromTable,
			isCreatedWithAi,
			isCreatedFromActionItems,
			pageMode,
			hasEditPermission,
		],
	);

	const logSidePanelIssueRemoved = useCallback(() => {
		if (isCreatedWithAi) {
			logAiResultRemoved();
		}
		const attributes = buildAttributes({
			baseAttributes: {},
			isCreatedWithAi,
			isCreatedFromTable,
			isCreatedFromActionItems,
			hasEditPermission,
			pageMode,
		});
		createAnalyticsEvent({
			type: 'sendTrackEvent',
			data: {
				source: getSource(isCreatedFromTable, isCreatedFromActionItems),
				action: 'removed',
				actionSubject: 'sidePanelIssue',
				attributes,
			},
		}).fire();
	}, [
		isCreatedWithAi,
		isCreatedFromTable,
		isCreatedFromActionItems,
		pageMode,
		createAnalyticsEvent,
		logAiResultRemoved,
		hasEditPermission,
	]);

	const logSidePanelIssueApproved = useCallback(
		(issueAction: ActionType) => {
			const attributes = buildAttributes({
				baseAttributes: { issueAction },
				isCreatedWithAi,
				isCreatedFromTable,
				isCreatedFromActionItems,
				hasEditPermission,
				pageMode,
			});
			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					source: getSource(isCreatedFromTable, isCreatedFromActionItems),
					action: 'approved',
					actionSubject: 'sidePanelIssue',
					attributes,
				},
			}).fire();
		},
		[
			createAnalyticsEvent,
			isCreatedWithAi,
			isCreatedFromTable,
			isCreatedFromActionItems,
			pageMode,
			hasEditPermission,
		],
	);

	const logCreateAllClick = useCallback(() => {
		if (isCreatedWithAi) {
			logAiResultActioned('createAll');
		}
		logSidePanelIssueApproved('createAll');
	}, [isCreatedWithAi, logAiResultActioned, logSidePanelIssueApproved]);

	const logCreateSingleIssueClick = useCallback(
		(issueId?: string) => {
			if (isCreatedWithAi) {
				logAiResultActioned('createSingleIssue', issueId);
			}
			logSidePanelIssueApproved('createSingleIssue');
		},
		[isCreatedWithAi, logAiResultActioned, logSidePanelIssueApproved],
	);

	const logAiIssueCreationFailed = useCallback(
		(numIssues: number, error: Error | unknown = '') => {
			const attributes = buildAttributes({
				baseAttributes: {
					errorMessage: getErrorMessage(error),
					numIssues,
				},
				isCreatedWithAi,
				isCreatedFromTable,
				isCreatedFromActionItems,
				hasEditPermission,
				pageMode,
			});
			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					source: getSource(isCreatedFromTable, isCreatedFromActionItems),
					action: 'failed',
					actionSubject: 'aiIssueCreation',
					attributes,
				},
			}).fire();
		},
		[
			createAnalyticsEvent,
			isCreatedWithAi,
			isCreatedFromTable,
			isCreatedFromActionItems,
			pageMode,
			hasEditPermission,
		],
	);

	const logAiInteractionDismissed = useCallback(() => {
		const attributes = buildAttributes({
			baseAttributes: {},
			isCreatedWithAi,
			isCreatedFromTable,
			isCreatedFromActionItems,
			hasEditPermission,
			pageMode,
		});
		trackAIInteractionDismiss({ attributes });
	}, [
		isCreatedFromTable,
		isCreatedWithAi,
		isCreatedFromActionItems,
		pageMode,
		trackAIInteractionDismiss,
		hasEditPermission,
	]);

	const logAiNoResultsGenerated = useCallback(() => {
		const attributes = buildAttributes({
			baseAttributes: {},
			isCreatedWithAi,
			isCreatedFromTable,
			isCreatedFromActionItems,
			hasEditPermission,
			pageMode,
		});
		createAnalyticsEvent({
			type: 'sendTrackEvent',
			data: {
				source: getSource(isCreatedFromTable, isCreatedFromActionItems),
				action: 'generated',
				actionSubject: 'aiNoResults',
				attributes,
			},
		}).fire();
	}, [
		createAnalyticsEvent,
		isCreatedFromTable,
		isCreatedWithAi,
		isCreatedFromActionItems,
		pageMode,
		hasEditPermission,
	]);

	const logAiInvalidResultsGenerated = useCallback(() => {
		const attributes = buildAttributes({
			baseAttributes: {},
			isCreatedWithAi,
			isCreatedFromTable,
			isCreatedFromActionItems,
			hasEditPermission,
			pageMode,
		});
		createAnalyticsEvent({
			type: 'sendTrackEvent',
			data: {
				source: getSource(isCreatedFromTable, isCreatedFromActionItems),
				action: 'generated',
				actionSubject: 'aiInvalidResults',
				attributes,
			},
		}).fire();
	}, [
		createAnalyticsEvent,
		isCreatedFromTable,
		isCreatedWithAi,
		isCreatedFromActionItems,
		pageMode,
		hasEditPermission,
	]);

	const logAiInteractionInitiated = useCallback(() => {
		const attributes = buildAttributes({
			baseAttributes: {},
			isCreatedWithAi,
			isCreatedFromTable,
			isCreatedFromActionItems,
			hasEditPermission,
			pageMode,
		});
		trackAIInteractionInit({ attributes });
	}, [
		isCreatedFromTable,
		isCreatedWithAi,
		isCreatedFromActionItems,
		pageMode,
		trackAIInteractionInit,
		hasEditPermission,
	]);

	const logAiStreamingBackendFailed = useCallback(
		(error: Error | unknown = '') => {
			const attributes = buildAttributes({
				baseAttributes: {},
				isCreatedWithAi,
				isCreatedFromTable,
				isCreatedFromActionItems,
				hasEditPermission,
				pageMode,
			});
			trackAIResultError({ aiErrorMessage: getErrorMessage(error) }, { attributes });
		},
		[
			isCreatedFromTable,
			isCreatedWithAi,
			isCreatedFromActionItems,
			pageMode,
			trackAIResultError,
			hasEditPermission,
		],
	);

	const logIssueCreationFailed = useCallback(
		(numIssues: number, error: Error | unknown = '') => {
			if (isCreatedWithAi) {
				logAiIssueCreationFailed(numIssues, error);
			}
			logMultipleIssueCreateFailure(numIssues, error);
		},
		[isCreatedWithAi, logAiIssueCreationFailed, logMultipleIssueCreateFailure],
	);

	const logSidePanelClosed = useCallback(() => {
		const attributes = buildAttributes({
			baseAttributes: {},
			isCreatedWithAi,
			isCreatedFromTable,
			isCreatedFromActionItems,
			hasEditPermission,
			pageMode,
		});
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: getSource(isCreatedFromTable, isCreatedFromActionItems),
				action: 'closed',
				actionSubject: 'modalDialog',
				actionSubjectId: 'linkCreate',
				attributes,
			},
		}).fire();
	}, [
		createAnalyticsEvent,
		isCreatedWithAi,
		isCreatedFromTable,
		isCreatedFromActionItems,
		pageMode,
		hasEditPermission,
	]);

	const logAddMappingClicked = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: 'confluenceHighlightActionsMenu',
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'addMapping',
				type: 'ui',
				attributes: {
					pageMode,
				},
			},
		}).fire();
	}, [createAnalyticsEvent, pageMode]);

	const logDeleteMappingClicked = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: 'confluenceHighlightActionsMenu',
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'deleteMapping',
				type: 'ui',
				attributes: {
					pageMode,
				},
			},
		}).fire();
	}, [createAnalyticsEvent, pageMode]);

	return {
		logSingleIssueCreateSuccess,
		logMultipleIssueCreateSuccess,
		logSingleIssueCreateFailure,
		logIssueInsertionFailure,
		logCancelAiPanel,
		logCreateAllClick,
		logCreateSingleIssueClick,
		logAiInteractionInitiated,
		logAiResultViewed,
		logAiResultRemoved,
		logAiInteractionDismissed,
		logAiNoResultsGenerated,
		logAiInvalidResultsGenerated,
		logRequestFailed,
		logAiStreamingBackendFailed,
		logSidePanelIssueRemoved,
		logSidePanelIssueApproved,
		logIssueCreationFailed,
		logSidePanelClosed,
		logAddMappingClicked,
		logDeleteMappingClicked,
	};
};
